import type * as monacoTypes from "monaco-editor/esm/vs/editor/editor.api";
import { Block, Ref, Resolver } from "../model/types";

const LANG_ID = "innSnip";

// segment of letters/numbers/_/-
const SEG = String.raw`[A-Za-z0-9_-]+`;
// allow dot OR slash separators (mixed is fine): a.b/c-d/72
export const IDENTIFIER = String.raw`${SEG}(?:[./]${SEG})*`;

// inner content only (no braces)
const INNER_RE = new RegExp(String.raw`^\s*(?:>(${IDENTIFIER})\s*\(\s*\)|>(${IDENTIFIER})|(${IDENTIFIER}))\s*$`);

function scanOutermostMustaches(line: string): { start: number; end: number; inner: string; raw: string }[] {
  const out: { start: number; end: number; inner: string; raw: string }[] = [];
  let depth = 0;
  let start = -1;

  for (let i = 0; i < line.length - 1; i++) {
    const two = line[i] + line[i + 1];

    if (two === "{{") {
      if (depth === 0) start = i; // index of first '{'
      depth++;
      i++; // skip the second '{'
      continue;
    }
    if (two === "}}") {
      if (depth > 0) depth--;
      if (depth === 0 && start !== -1) {
        const end = i + 2; // index after closing braces
        const raw = line.slice(start, end);
        const inner = raw.slice(2, -2).trim();
        out.push({ start, end, inner, raw });
        start = -1;
      }
      i++; // skip the second '}'
      continue;
    }
  }
  return out;
}
/** Scan OUTERMOST {{ ... }} across an entire string */
function scanOutermostMustachesText(src: string): { start: number; end: number; inner: string; raw: string }[] {
  const out: { start: number; end: number; inner: string; raw: string }[] = [];
  let depth = 0,
    start = -1;

  for (let i = 0; i < src.length - 1; i++) {
    const two = src[i] + src[i + 1];

    if (two === "{{") {
      if (depth === 0) start = i;
      depth++;
      i++;
      continue;
    }
    if (two === "}}") {
      if (depth > 0) depth--;
      if (depth === 0 && start !== -1) {
        const end = i + 2;
        const raw = src.slice(start, end);
        const inner = raw.slice(2, -2).trim();
        out.push({ start, end, inner, raw });
        start = -1;
      }
      i++;
      continue;
    }
  }
  return out;
}

const containsPos = (r: monacoTypes.IRange, p: monacoTypes.IPosition): boolean => {
  if (p.lineNumber < r.startLineNumber || p.lineNumber > r.endLineNumber) return false;
  if (r.startLineNumber === r.endLineNumber) {
    return p.column >= r.startColumn && p.column <= r.endColumn;
  }
  if (p.lineNumber === r.startLineNumber) return p.column >= r.startColumn;
  if (p.lineNumber === r.endLineNumber) return p.column <= r.endColumn;
  return true;
};

const rangeInside = (inner: monacoTypes.IRange, outer: monacoTypes.IRange): boolean => {
  const startsAfter =
    inner.startLineNumber > outer.startLineNumber ||
    (inner.startLineNumber === outer.startLineNumber && inner.startColumn >= outer.startColumn);
  const endsBefore =
    inner.endLineNumber < outer.endLineNumber ||
    (inner.endLineNumber === outer.endLineNumber && inner.endColumn <= outer.endColumn);
  return startsAfter && endsBefore;
};

const innerForRef = (r: Ref) => (r.kind === "path" ? r.full : r.kind === "include" ? `>${r.name}` : `>${r.name}()`);

const originalFromInner = (inner: string) => `{{${inner}}}`;

export function parseRefs(model: monacoTypes.editor.ITextModel): Ref[] {
  const refs: Ref[] = [];

  for (let ln = 1; ln <= model.getLineCount(); ln++) {
    const text = model.getLineContent(ln);

    for (const hit of scanOutermostMustaches(text)) {
      const startColumn = hit.start + 1; // monaco is 1-based
      const endColumn = startColumn + hit.raw.length;
      const range: monacoTypes.IRange = {
        startLineNumber: ln,
        startColumn,
        endLineNumber: ln,
        endColumn
      };

      // 1) try strict IDENT-based parsing (your original rules)
      const m = hit.inner.match(INNER_RE);
      if (m) {
        if (m[1]) refs.push({ kind: "exec", name: m[1], full: hit.raw, range });
        else if (m[2]) refs.push({ kind: "include", name: m[2], full: hit.raw, range });
        else refs.push({ kind: "path", full: m[3], range });
        continue;
      }

      // 2) otherwise, fall back to relaxed detection so nested stays intact
      //    e.g. "{{>snip-{{context.type}}}}" -> include with name "snip-{{context.type}}"
      const execLoose = hit.inner.match(/^\s*>\s*(.+?)\s*\(\s*\)\s*$/);
      if (execLoose) {
        refs.push({ kind: "exec", name: execLoose[1].trim(), full: hit.raw, range });
        continue;
      }
      const includeLoose = hit.inner.match(/^\s*>\s*(.+?)\s*$/);
      if (includeLoose) {
        refs.push({ kind: "include", name: includeLoose[1].trim(), full: hit.raw, range });
        continue;
      }

      // 3) last resort: treat as path-like content (no name)
      refs.push({ kind: "path", full: hit.inner.trim(), range });
    }
  }

  return refs;
}

export function parseExpandedBlocks(model: monacoTypes.editor.ITextModel): Block[] {
  const src = model.getValue();
  const hits = scanOutermostMustachesText(src);

  // key -> stack of start tokens (supports nested same-name blocks if you ever need it)
  const stacks = new Map<string, { start: number; end: number }[]>();
  const blocks: Block[] = [];

  for (const hit of hits) {
    const s = hit.inner.trim();

    // {{ <key> START}}
    let m = s.match(/^(.+?)\s+START\s*$/);
    if (m) {
      const key = m[1].trim();
      let stack = stacks.get(key);
      if (!stack) stacks.set(key, (stack = []));
      stack.push({ start: hit.start, end: hit.end });
      continue;
    }

    // {{ <key> END}}
    m = s.match(/^(.+?)\s+END\s*$/);
    if (m) {
      const key = m[1].trim();
      const stack = stacks.get(key);
      const opener = stack?.pop();
      if (!opener) continue; // unmatched END; ignore

      const os = model.getPositionAt(opener.start);
      const oe = model.getPositionAt(opener.end);
      const cs = model.getPositionAt(hit.start);
      const ce = model.getPositionAt(hit.end);

      blocks.push({
        inner: key, // e.g. ">test-{{context.projectStage}}"
        openRange: {
          startLineNumber: os.lineNumber,
          startColumn: os.column,
          endLineNumber: oe.lineNumber,
          endColumn: oe.column
        },
        closeRange: {
          startLineNumber: cs.lineNumber,
          startColumn: cs.column,
          endLineNumber: ce.lineNumber,
          endColumn: ce.column
        },
        range: {
          startLineNumber: os.lineNumber,
          startColumn: os.column,
          endLineNumber: ce.lineNumber,
          endColumn: ce.column
        }
      });
    }
  }

  return blocks;
}

function resolveRefOneLevel(r: Ref, resolver: Resolver): string {
  switch (r.kind) {
    case "path":
      return resolver.path(r.full ?? "");
    case "include":
      return resolver.include(r.name);
    case "exec":
      return resolver.exec(r.name);
  }
}

function expandRefEdit(r: Ref, resolver: Resolver): monacoTypes.editor.IIdentifiedSingleEditOperation {
  const inner = innerForRef(r);
  const base = resolveRefOneLevel(r, resolver); // one pass only
  const wrapped = `{{${inner} START}}${base}{{${inner} END}}`;
  return { range: r.range, text: wrapped };
}

function collapseBlockEdit(b: Block): monacoTypes.editor.IIdentifiedSingleEditOperation {
  return { range: b.range, text: originalFromInner(b.inner) };
}

export function registerSnip(monaco: typeof monacoTypes, resolver: Resolver) {
  monaco.languages.register({ id: LANG_ID });

  monaco.languages.setLanguageConfiguration(LANG_ID, {
    wordPattern: /[\w.>]+/g
  });

  monaco.languages.setMonarchTokensProvider(LANG_ID, {
    defaultToken: "",
    tokenizer: {
      root: [[/\{\{/, { token: "delimiter.brace", next: "@inside" }]],
      inside: [
        [/\}\}/, { token: "delimiter.brace", next: "@pop" }],
        [/\s+/, "white"],
        [
          /(>)([A-Za-z_][A-Za-z0-9_/-]*(?:[./][A-Za-z_][A-Za-z0-9_/-]*)*)(\s*\(\s*\))/,
          ["operator", "directive.exec.name", "delimiter.parenthesis"]
        ],
        [/(>)([A-Za-z_][A-Za-z0-9_-]*(?:\.[A-Za-z_][A-Za-z0-9_-]*)*)/, ["operator", "directive.include.name"]],
        [/([A-Za-z_][A-Za-z0-9_-]*(?:\.[A-Za-z_][A-Za-z0-9_-]*)*)/, "variable.path"],
        [/[^}]+/, ""]
      ]
    }
  });

  monaco.editor.defineTheme("innSnipTheme", {
    base: "vs",
    inherit: true,
    rules: [
      { token: "delimiter.brace", foreground: "6b7280" },
      { token: "delimiter.parenthesis", foreground: "6b7280" },
      { token: "operator", foreground: "8b5cf6" },
      { token: "directive.exec.name", foreground: "6f42c1", fontStyle: "bold" },
      { token: "directive.include.name", foreground: "0b7285" },
      { token: "variable.path", foreground: "0050b3" }
    ],
    colors: { "editor.background": "#ffffff" }
  });

  monaco.editor.setTheme("innSnipTheme");

  monaco.languages.registerHoverProvider(LANG_ID, {
    provideHover(model, position) {
      const blocks = parseExpandedBlocks(model);
      const blk = blocks.find((b) => containsPos(b.range, position));
      if (blk) {
        const label = originalFromInner(blk.inner);
        return { range: blk.range, contents: [{ value: `**Expanded Block** ${label}` }] };
      }
      const refs = parseRefs(model);
      const ref = refs.find((r) => containsPos(r.range, position));
      if (!ref) return null;
      const one = resolveRefOneLevel(ref, resolver);
      return { range: ref.range, contents: [{ value: `**Preview (1 level)**\n\`\`\`text\n${one}\n\`\`\`` }] };
    }
  });
}

// helper — treat {{ <inner> START}} / {{ <inner> END}} as block markers
const isBlockMarkerRef = (r: Ref): boolean => {
  // build the inner string we would have inside {{ ... }}
  const inner = innerForRef(r); // path -> "a.b", include -> ">name", exec -> ">name()"
  // if the inner ends with START/END (allow trailing spaces), it's a marker
  return /\s(?:START|END)\s*$/.test(inner);
};

export function installSnipToggle(
  editor: monacoTypes.editor.IStandaloneCodeEditor,
  _monaco: typeof monacoTypes,
  resolver: Resolver
) {
  const d1 = editor.onMouseDown((e) => {
    if (!e.target.position) return;
    const model = editor.getModel();
    if (!model) return;

    const pos = e.target.position;
    const blocks = parseExpandedBlocks(model);
    const refs = parseRefs(model);

    // Find innermost block containing the click (so nested blocks collapse first)
    let hitBlock: Block | undefined;
    {
      const candidates = blocks.filter((b) => containsPos(b.range, pos));

      if (candidates.length) {
        // choose the smallest (innermost) by range length
        const withLens = candidates.map((b) => {
          const start = model.getOffsetAt({ lineNumber: b.range.startLineNumber, column: b.range.startColumn });
          const end = model.getOffsetAt({ lineNumber: b.range.endLineNumber, column: b.range.endColumn });
          return { b, len: end - start };
        });
        withLens.sort((a, b) => a.len - b.len);
        hitBlock = withLens[0].b;
      }
    }

    // If inside a block:
    if (hitBlock) {
      const nestedCandidates = refs.filter(
        (r) => containsPos(r.range, pos) && rangeInside(r.range, hitBlock?.range) && !isBlockMarkerRef(r)
      );

      // choose the smallest (innermost) by range length
      let nestedRef: Ref | undefined;
      if (nestedCandidates.length) {
        const withLens = nestedCandidates.map((r) => {
          const start = model.getOffsetAt({ lineNumber: r.range.startLineNumber, column: r.range.startColumn });
          const end = model.getOffsetAt({ lineNumber: r.range.endLineNumber, column: r.range.endColumn });
          return { r, len: end - start };
        });
        withLens.sort((a, b) => a.len - b.len);
        nestedRef = withLens[0].r;
      }

      if (nestedRef) {
        model.pushEditOperations([], [expandRefEdit(nestedRef, resolver)], () => null);
        return;
      }

      // Otherwise collapse the whole block
      model.pushEditOperations([], [collapseBlockEdit(hitBlock)], () => null);
      return;
    }
    const ref = refs.find((r) => containsPos(r.range, pos));

    if (!ref) return;
    model.pushEditOperations([], [expandRefEdit(ref, resolver)], () => null);
  });

  return { dispose: () => d1.dispose() };
}

let bgDecoIds: string[] = [];
export function renderSnipTokenBackgrounds(editor: monacoTypes.editor.IStandaloneCodeEditor) {
  const model = editor.getModel();
  if (!model) return;
  const refs = parseRefs(model);
  const decos: monacoTypes.editor.IModelDeltaDecoration[] = refs.map((r) => ({
    range: r.range,
    options: {
      inlineClassName: r.kind === "exec" ? "snip-bg-exec" : r.kind === "include" ? "snip-bg-include" : "snip-bg-path"
    }
  }));
  bgDecoIds = model.deltaDecorations(bgDecoIds, decos);
}
